// ─────────────────────────────────────────────────────────────
// printConfig.js — Noble Enterprises (tape module) print engine
// Letterhead, palette, layout constants, formatters, terms.
// This file is the ONLY place branding/styling is defined.
// ─────────────────────────────────────────────────────────────

// ─── Company letterhead ──────────────────────────────────────
// TODO: replace with real Noble Enterprises details
import { NOBLE_LOGO_DATA_URL } from './noble-logo';

export const COMPANY = {
  name: 'Noble Enterprises',
  tagline: 'Adhesive Tape Manufacturing',
  address: 'Faisalabad, Pakistan',
  phone: '+92 300 0000000',
  landline: '041-0000000',
  email: 'info@nobleenterprises.pk',
  website: 'www.nobleenterprises.pk',
  strn: '0000000000000',
  ntn: '0000000-0',
};

// ─── Logo ────────────────────────────────────────────────────
// dataUrl === null  → engine draws a bordered box with `initials`
// dataUrl set       → engine draws the image at the same position
// The mark only — the NE and the tape roll. The wordmark below it in
// the supplied artwork was left out because drawHeader already prints
// COMPANY.name beside the logo, and both would state the name twice
// at two different sizes.
//
// It is 1.82:1, so width and height are not the same number. Making
// them equal would squash it. drawHeader starts the company name at
// mx + LOGO.width + 4, so a wider logo moves the text across on its
// own — which is why this is kept close to the old 12mm footprint
// rather than as large as it could be.
export const LOGO = {
  dataUrl: NOBLE_LOGO_DATA_URL,
  format: 'JPEG',
  initials: 'NE',
  width: 18.2,
  height: 10,
};

// ─── Palette (RGB triplets for jsPDF) ────────────────────────
export const PALETTE = {
  ink: [40, 40, 40],
  inkSoft: [60, 60, 60],
  muted: [90, 90, 90],
  faint: [150, 150, 150],
  navy: [44, 62, 80],
  heading: [26, 60, 110],

  border: [150, 150, 150],
  rule: [40, 40, 40],

  headFill: [232, 232, 232],
  footFill: [245, 245, 245],
  grandFill: [240, 240, 240],
  zebra: [250, 250, 250],

  // tone colours — ledgers, P&L sections
  credit: [21, 128, 61],
  debit: [185, 28, 28],
  income: [21, 128, 61],
  incomeFill: [235, 247, 238],
  expense: [185, 28, 28],
  expenseFill: [253, 240, 240],
  net: [26, 60, 110],
  netFill: [232, 238, 247],
};

// ─── Layout constants (mm) ───────────────────────────────────
export const LAYOUT = {
  marginX: 12,
  headerTop: 14,
  boxHeight: 34,
  boxGap: 6,
    totalsBoxWidth: 70,
  rowGap: 6,
  // Gap between a box label and its value. Was a fixed 29mm,
  // which left "Phone" marooned from its number. The engine now
  // measures the longest label in each box and uses that.
      labelPad: 6,
  // Space between the first label/value pair and the second one
  // on the same row.
  colGap: 10,
  signatureWidth: 60,
  bottomSafe: 18,
};

// Bumped from 8pt. At A4 a table of six columns has room to
// spare, and 8pt read as fine print on a document a customer is
// meant to check. Every report shares these, so the change is
// deliberately small — big enough to read, not so big that a
// wide landscape report starts wrapping.
export const FONTS = {
  title: 18,
  boxHeading: 9.5,
  label: 8.5,
  body: 9.5,
  small: 7.5,
  tiny: 7,
  totals: 9,
  totalsEmphasis: 11,
};

// ─── Formatters ──────────────────────────────────────────────
// Referenced by name from report configs: { key, label, fmt: 'money' }
const toNum = (n) => (isNaN(parseFloat(n)) ? 0 : parseFloat(n));

export const FORMATTERS = {
  text: (v) => (v === null || v === undefined || v === '' ? '—' : String(v)),

  int: (v) => Math.round(toNum(v)).toLocaleString('en-PK'),

  money: (v) => {
    const n = Math.round(toNum(v));
    const s = Math.abs(n).toLocaleString('en-PK');
    return n < 0 ? `(${s})` : s;
  },

  money2: (v) =>
    toNum(v).toLocaleString('en-PK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),

  qty: (v) => toNum(v).toFixed(2).replace(/\.?0+$/, ''),

  kg: (v) => `${toNum(v).toFixed(2).replace(/\.?0+$/, '')} kg`,

  meters: (v) => {
    const n = toNum(v);
    return `${n.toLocaleString('en-PK', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })} m`;
  },

  pct: (v) => {
    const n = toNum(v);
    const s = `${Math.abs(n).toFixed(2).replace(/\.?0+$/, '')}%`;
    return n < 0 ? `(${s})` : s;
  },

  rate: (v) =>
    toNum(v).toLocaleString('en-PK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }),

  date: (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  },

  datetime: (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '—';
    return `${d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  },
};

export const applyFmt = (value, fmt) => {
  if (typeof fmt === 'function') return fmt(value);
  const fn = FORMATTERS[fmt] || FORMATTERS.text;
  return fn(value);
};

// ─── Default terms ───────────────────────────────────────────
export const TERMS_PO = [
  '1) Credit days will start after receiving invoices.',
  '2) We reserve the right to cancel this PO at any time without intimation.',
  '3) No payments, gifts or gratuities of any kind shall be made directly or indirectly to Noble Enterprises employees.',
  '4) Delivery must be as per the agreed lead time.',
  '5) Vendor is responsible for proper packing of the goods.',
  '6) Vendor shall bear the expense of loss or damage of goods until delivered to destination.',
];

export const TERMS_INVOICE = [
  '1) Goods once sold are not returnable without prior written approval.',
  '2) Claims regarding quantity or quality must be raised within 3 days of receipt.',
  '3) Payment as per agreed credit terms; overdue balances may attract a service charge.',
  '4) This invoice is computer generated and valid without signature.',
];

// ─── Default signature blocks ────────────────────────────────
export const SIGNATURES = {
  po: ['Order By', 'Approved By', 'Audit Approved By'],
  invoice: ['Prepared By', 'Received By'],
  report: ['Prepared By', 'Checked By'],
};

// ─── Footer ──────────────────────────────────────────────────
export const FOOTER = {
  left: () => `Generated ${FORMATTERS.datetime(new Date())}`,
  right: (page, total) => `Page ${page} of ${total}`,
};